/**
 * US-65: user-menu (правый верхний угол).
 *
 * - Аватар-кружок с инициалами (2 буквы из full_name, либо первая буква
 *   username если full_name пуст).
 * - Dropdown: «Профиль / Сменить пароль / Выйти».
 * - «Профиль» открывает <ProfileModal>, «Сменить пароль» — <ChangePasswordModal>.
 * - «Выйти» — performLogout() (как раньше из sidebar).
 *
 * Цвет аватара — indigo (#5b6be0) из Direction C, для импесонированной
 * сессии перекрашивается в оранжевый чтобы было заметно.
 */

import { useState } from "react";
import { Avatar, Menu, UnstyledButton, useMantineColorScheme } from "@mantine/core";
import { LogOut, Moon, Settings, ShieldCheck, Sun, UserCircle } from "lucide-react";

import type { Me } from "../api/client";
import { performLogout } from "./LoginScreen";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { ProfileModal } from "./ProfileModal";

interface Props {
  me: Me;
}

function getInitials(me: Me): string {
  const name = (me.full_name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  return me.username.slice(0, 2).toUpperCase();
}

export function UserMenu({ me }: Props) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const initials = getInitials(me);
  const isHijacked = me.is_hijacked;
  // US-100: переключатель темы переехал сюда из убранного нижнего меню сайдбара.
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";

  return (
    <>
      <Menu position="bottom-end" withArrow shadow="md" width={220}>
        <Menu.Target>
          <UnstyledButton aria-label="Меню пользователя" data-testid="user-menu-trigger">
            <Avatar size={32} radius="xl" color={isHijacked ? "orange" : "indigo"} variant="filled">
              {initials}
            </Avatar>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>
            {me.full_name || me.username}
            {me.email && (
              <div style={{ fontSize: 11, color: "var(--mantine-color-dimmed)", marginTop: 2 }}>
                {me.email}
              </div>
            )}
          </Menu.Label>
          <Menu.Item
            leftSection={<UserCircle size={14} />}
            onClick={() => setProfileOpen(true)}
            data-testid="user-menu-profile"
          >
            Профиль
          </Menu.Item>
          <Menu.Item
            leftSection={<Settings size={14} />}
            onClick={() => setPwOpen(true)}
            data-testid="user-menu-change-password"
          >
            Сменить пароль
          </Menu.Item>
          <Menu.Item
            leftSection={dark ? <Sun size={14} /> : <Moon size={14} />}
            onClick={toggleColorScheme}
            closeMenuOnClick={false}
            data-testid="user-menu-theme"
          >
            {dark ? "Светлая тема" : "Тёмная тема"}
          </Menu.Item>
          {(me.is_staff || me.is_superuser) && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={<ShieldCheck size={14} />}
                component="a"
                href="/admin/"
                data-testid="user-menu-admin"
              >
                Администрирование
              </Menu.Item>
            </>
          )}
          <Menu.Divider />
          <Menu.Item
            leftSection={<LogOut size={14} />}
            color="red"
            onClick={() => void performLogout()}
            data-testid="user-menu-logout"
          >
            Выйти
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <ProfileModal opened={profileOpen} onClose={() => setProfileOpen(false)} me={me} />
      <ChangePasswordModal opened={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}
